import { useCallback, useEffect, useRef, useState } from 'react';
import { getDraft, putDraft } from '@/api/drafts';
import { currentUserKey } from '@/utils/session';

/**
 * The unsent question of each Q&A (spec §3): kept in this browser at once
 * and on the server after a pause in typing, so it survives a reload and
 * follows the user to another browser. Scoped to the user and the Q&A.
 *
 * Every save carries `client_saved_at`, the moment the text was typed, and
 * the question carries `client_sent_at`; before a send, the pending save is
 * cancelled and an in-flight one awaited, so a late save can never bring
 * the sent text back (the server refuses older saves with 409 draft_stale).
 */

export const DRAFT_SAVE_DELAY_MS = 1000;
export const DRAFT_STORAGE_PREFIX = 'qa-assist.draft.v1:';

interface LocalDraft {
  text: string;
  /** When it was typed (ISO, this browser's clock). */
  savedAt: string;
  /** The server has it. */
  synced: boolean;
  /**
   * This browser sent this text at that moment (the send may still be on
   * its way). Only then may a stale save take it out of the field: a send
   * from another device is judged by that device's clock.
   */
  sentAt?: string;
}

const storageKey = (inquiryId: string) =>
  `${DRAFT_STORAGE_PREFIX}${currentUserKey() ?? 'anonymous'}:${inquiryId}`;

export const readLocalDraft = (inquiryId: string): LocalDraft | null => {
  try {
    const raw = localStorage.getItem(storageKey(inquiryId));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<LocalDraft>;
    return typeof value.text === 'string' && typeof value.savedAt === 'string'
      ? {
          text: value.text,
          savedAt: value.savedAt,
          synced: !!value.synced,
          ...(typeof value.sentAt === 'string' ? { sentAt: value.sentAt } : {}),
        }
      : null;
  } catch {
    return null;
  }
};

const writeLocalDraft = (inquiryId: string, draft: LocalDraft | null) => {
  try {
    if (!draft || !draft.text) localStorage.removeItem(storageKey(inquiryId));
    else localStorage.setItem(storageKey(inquiryId), JSON.stringify(draft));
  } catch {
    // Storage full or blocked: the server copy still works.
  }
};

const time = (iso: string | null | undefined) => {
  const value = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(value) ? value : 0;
};

/**
 * Which text to show after the server's draft arrived: unsaved local edits
 * win unless a send cleared the draft after they were typed, or the server
 * holds text typed after them (on another device); otherwise the server's
 * text, or nothing when the server has none (it was sent or cleared
 * elsewhere).
 */
export const reconcileDraft = (
  local: LocalDraft | null,
  server: {
    text: string | null;
    updated_at: string | null;
    client_saved_at?: string | null;
    cleared_at: string | null;
  } | null,
): { text: string; push: boolean } => {
  const localTime = local ? time(local.savedAt) : 0;
  // Unsaved local text wins, unless a send cleared the draft after it was
  // typed, or the stored text was typed after it. These times all come from
  // client clocks (client_sent_at, client_saved_at); the server's
  // updated_at is another clock and is not compared. Without a stored
  // client_saved_at (older API, or a broken clock) local text wins.
  if (local && !local.synced && local.text) {
    if (
      localTime > time(server?.cleared_at) &&
      !(server?.text && time(server.client_saved_at) > localTime)
    ) {
      return { text: local.text, push: true };
    }
    return { text: server?.text ?? '', push: false };
  }
  return { text: server?.text ?? '', push: false };
};

interface Pending {
  inquiryId: string;
  text: string;
  savedAt: string;
}

/**
 * Drafts of other users of this browser (kept when a session expired) go
 * when someone signs in: only the user who typed them may get them back.
 */
const purgeOtherUsersDrafts = () => {
  const user = currentUserKey();
  // Not known (yet): the user's own drafts could look like someone else's.
  if (!user) return;
  const own = `${DRAFT_STORAGE_PREFIX}${user}:`;
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(DRAFT_STORAGE_PREFIX) && !key.startsWith(own)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage blocked: nothing kept either.
  }
};

export const useDraft = (inquiryId: string | undefined) => {
  const [text, setTextState] = useState('');
  const textRef = useRef(text);
  textRef.current = text;
  const [loaded, setLoaded] = useState(false);
  const inquiryRef = useRef(inquiryId);
  const pending = useRef<Pending | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** The user typed since this Q&A was opened: the server's copy must not replace it. */
  const edited = useRef(false);

  const save = useCallback((entry: Pending): Promise<void> => {
    const run = (async () => {
      const result = await putDraft(
        entry.inquiryId,
        entry.text || null,
        entry.savedAt,
      );
      const local = readLocalDraft(entry.inquiryId);
      const same = !!local && local.savedAt === entry.savedAt;
      if (result.kind === 'saved' && same) {
        writeLocalDraft(entry.inquiryId, { ...local, synced: true });
      }
      // Refused as older than a send. Only a send of this text from this
      // browser makes it obsolete (a reload during the send restored it);
      // after a send elsewhere, by another clock, the text stays.
      const sentHere = !!local?.sentAt && local.text === entry.text;
      if (result.kind === 'stale' && sentHere) {
        if (same) writeLocalDraft(entry.inquiryId, null);
        if (
          inquiryRef.current === entry.inquiryId &&
          textRef.current === entry.text
        ) {
          setTextState('');
        }
      }
    })();
    return run;
  }, []);

  /** Save what is pending now (switching Q&As, leaving the page). */
  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const entry = pending.current;
    pending.current = null;
    if (entry) void save(entry);
  }, [save]);

  const schedule = useCallback(
    (entry: Pending) => {
      pending.current = entry;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        const next = pending.current;
        pending.current = null;
        if (next) void save(next);
      }, DRAFT_SAVE_DELAY_MS);
    },
    [save],
  );

  useEffect(() => {
    purgeOtherUsersDrafts();
  }, []);

  // Open a Q&A: the local copy at once, then the server's.
  useEffect(() => {
    inquiryRef.current = inquiryId;
    edited.current = false;
    setLoaded(false);
    if (!inquiryId) {
      setTextState('');
      return;
    }
    const local = readLocalDraft(inquiryId);
    setTextState(local?.text ?? '');
    let active = true;
    void getDraft(inquiryId).then(result => {
      if (!active) return;
      setLoaded(true);
      if (result.kind !== 'ok' || edited.current) return;
      const next = reconcileDraft(readLocalDraft(inquiryId), result.draft);
      setTextState(next.text);
      const current = readLocalDraft(inquiryId);
      if (next.push && current) {
        schedule({ inquiryId, text: current.text, savedAt: current.savedAt });
      } else if (!next.push) {
        writeLocalDraft(
          inquiryId,
          next.text
            ? {
                text: next.text,
                savedAt: result.draft?.updated_at ?? new Date().toISOString(),
                synced: true,
              }
            : null,
        );
      }
    });
    return () => {
      active = false;
      // Leaving the Q&A: its pending save goes now.
      flush();
    };
  }, [inquiryId, flush, schedule]);

  // Leaving the page: keep the local copy; the save is best effort.
  useEffect(() => {
    const onHide = () => flush();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [flush]);

  const setText = useCallback(
    (value: string) => {
      const id = inquiryRef.current;
      setTextState(value);
      if (!id) return;
      edited.current = true;
      const savedAt = new Date().toISOString();
      writeLocalDraft(id, { text: value, savedAt, synced: false });
      schedule({ inquiryId: id, text: value, savedAt });
    },
    [schedule],
  );

  /**
   * When Send is pressed: the send time, from the same clock as the saves,
   * and the pending save dropped (its text is being sent). A save already
   * on its way is not waited for: it was typed earlier than this send, so
   * the server either stores it before the send clears it or refuses it
   * (409 draft_stale) after.
   */
  const prepareSend = useCallback((inquiry: string, sentText: string) => {
    const sentAt = new Date().toISOString();
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    pending.current = null;
    const local = readLocalDraft(inquiry);
    if (local && local.text === sentText) {
      writeLocalDraft(inquiry, { ...local, sentAt });
    }
    return sentAt;
  }, []);

  /** The question was accepted: its text is no longer a draft. */
  const sent = useCallback((inquiry: string, sentText: string) => {
    const local = readLocalDraft(inquiry);
    if (!local || local.text === sentText) writeLocalDraft(inquiry, null);
    if (inquiryRef.current === inquiry) {
      setTextState(current => (current === sentText ? '' : current));
    }
  }, []);

  /** The question was refused: keep saving the text as a draft. */
  const resume = useCallback(
    (inquiry: string) => {
      const local = readLocalDraft(inquiry);
      if (local?.sentAt) {
        const { sentAt: _sent, ...rest } = local;
        writeLocalDraft(inquiry, rest);
      }
      if (local && !local.synced) {
        schedule({
          inquiryId: inquiry,
          text: local.text,
          savedAt: local.savedAt,
        });
      }
    },
    [schedule],
  );

  return { text, setText, loaded, prepareSend, sent, resume };
};
